/* FASTLANE automatic gifts. Shopify owns prices; this file never sets them.
 * All theme fetch cart mutations are serialized and reconciled before rendering.
 * Configuration and live inventory come from Shopify Liquid/product endpoints.
 */
(() => {
  'use strict';
  const node = document.getElementById('FastlaneGiftConfig');
  if (!node || window.FastlaneGifts) return;
  const config = JSON.parse(node.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const nativeFetch = window.fetch.bind(window);
  const handles = { tee: 'free-gift-t-shirt', hoodie: 'free-gift-hoodie', poster: 'free-gift-poster' };
  const products = config.products;
  const choices = {};
  let queue = Promise.resolve(), pending = 0, messages = [], blocked = false;
  const giftKind = item => Object.keys(handles).find(k => item.handle === handles[k] ||
    (products[k]?.id && Number(item.product_id) === Number(products[k].id)));
  const eligible = cart => {
    const paid = cart.items.filter(i => !giftKind(i) && i.final_line_price > 0);
    const count = paid.reduce((n, i) => n + i.quantity, 0);
    const orderDiscount = (cart.cart_level_discount_applications || []).reduce((n, d) => n + (d.total_allocated_amount || 0), 0);
    const amount = Math.max(0, paid.reduce((n, i) => n + i.final_line_price, 0) - orderDiscount);
    return { tee: count >= config.minItems,
      hoodie: cart.currency === config.currency && amount >= config.minAmount,
      poster: cart.currency === config.currency && amount >= config.minAmount };
  };
  async function request(path, body) {
    const response = await nativeFetch(root + path, body === undefined ? { cache: 'no-store' } : {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || data.message || 'Cart update failed.');
    return data;
  }
  function announce() {
    document.querySelectorAll('[data-fastlane-gift-message]').forEach(el => {
      el.textContent = [...new Set(messages)].join(' ');
      el.hidden = !el.textContent;
    });
  }
  function enqueue(task) {
    pending++;
    document.documentElement.classList.add('fastlane-gifts-busy');
    const job = queue.then(task);
    queue = job.catch(() => {});
    return job.finally(() => {
      pending--;
      if (!pending) document.documentElement.classList.remove('fastlane-gifts-busy');
      setTimeout(announce, 0);
    });
  }
  async function updates(changes) {
    if (Object.keys(changes).length) await request('cart/update.js', { updates: changes });
  }
  async function reconcile() {
    messages = [];
    let cart = await request('cart.js');
    let goals = eligible(cart);
    const trims = {};
    const seen = new Set();
    for (const item of cart.items) {
      const kind = giftKind(item);
      if (!kind) continue;
      if (!goals[kind] || seen.has(kind)) trims[item.key] = 0;
      else {
        seen.add(kind);
        choices[kind] = item.variant_id;
        if (item.quantity !== 1) trims[item.key] = 1;
      }
    }
    await updates(trims);
    if (Object.keys(trims).length) cart = await request('cart.js');
    goals = eligible(cart);
    for (const kind of Object.keys(handles)) {
      if (!goals[kind] || cart.items.some(i => giftKind(i) === kind)) continue;
      try {
        // A fresh product read prevents choosing a sold-out variant from stale page data.
        const product = await request('products/' + handles[kind] + '.js');
        products[kind] = product;
        const variant = product.variants.find(v => v.id === choices[kind] && v.available) || product.variants.find(v => v.available);
        if (!variant) { messages.push('A gift is currently out of stock.'); continue; }
        await request('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: { _fastlane_gift: kind } }] });
        choices[kind] = variant.id;
        cart = await request('cart.js');
      } catch (error) {
        messages.push('A gift could not be added. Please try again or contact us.');
        console.warn('[FASTLANE gifts]', error.message);
      }
    }
    // Discount combinations can alter other lines. Remove charged/unqualified gifts
    // until stable, without re-adding them during this pass (no discount oscillation).
    for (let pass = 0; pass < 4; pass++) {
      cart = await request('cart.js');
      goals = eligible(cart);
      const remove = {};
      for (const item of cart.items) {
        const kind = giftKind(item);
        if (!kind) continue;
        if (!goals[kind] || item.final_line_price !== 0) {
          remove[item.key] = 0;
          if (item.final_line_price !== 0) messages.push('A gift could not be confirmed as free and was not included. Please contact us.');
        }
      }
      if (!Object.keys(remove).length) { blocked = false; return cart; }
      await updates(remove);
    }
    throw new Error('Gift validation did not finish.');
  }
  function sectionIds() {
    return [...new Set(['cart-drawer', 'cart-icon-bubble',
      document.getElementById('main-cart-items')?.dataset.id,
      document.getElementById('main-cart-footer')?.dataset.id].filter(Boolean))];
  }
  async function sections(ids) {
    const query = new URLSearchParams({ sections: ids.join(',') });
    const response = await nativeFetch(root + 'cart?' + query, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart display could not refresh.');
    const data = await response.json();
    if (ids.some(id => typeof data[id] !== 'string')) throw new Error('Cart display could not refresh.');
    return data;
  }
  function render(cart, html) {
    const focused = document.activeElement;
    const focusedHandle = focused?.dataset?.giftHandle;
    const drawer = document.querySelector('cart-drawer');
    const wasOpen = drawer?.classList.contains('active');
    for (const [id, markup] of Object.entries(html)) {
      if (!markup) continue;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      if (id === 'cart-drawer') {
        const target = document.getElementById('CartDrawer'), source = doc.getElementById('CartDrawer');
        if (target && source) target.innerHTML = source.innerHTML;
      } else if (id === 'cart-icon-bubble') {
        const target = document.getElementById(id), source = doc.querySelector('.shopify-section');
        if (target && source) target.innerHTML = source.innerHTML;
      } else {
        for (const targetId of ['main-cart-items', 'main-cart-footer']) {
          const target = document.getElementById(targetId), source = doc.getElementById(targetId);
          if (target && source) target.innerHTML = source.innerHTML;
        }
      }
    }
    drawer?.classList.toggle('is-empty', cart.item_count === 0);
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    if (wasOpen && typeof window.trapFocus === 'function') {
      const target = focusedHandle ? drawer.querySelector(`[data-gift-handle="${focusedHandle}"]`) : null;
      window.trapFocus(drawer.querySelector('.drawer__inner'), target || drawer.querySelector('.drawer__close'));
    }
    announce();
  }
  function failed(error) {
    blocked = true;
    messages = ['Your bag could not be verified. Refresh the page before ordering.'];
    announce();
    console.error('[FASTLANE gifts]', error);
  }
  // Match only same-origin Ajax cart writes, not analytics or app order requests.
  window.fetch = function(input, init) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = (init?.method || input?.method || 'GET').toUpperCase();
    const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    if (url.origin === location.origin && method === 'GET' && /\/cart(?:\.js)?\/?$/.test(url.pathname) && pending) {
      return queue.then(() => nativeFetch(input, init));
    }
    if (url.origin !== location.origin || method !== 'POST' || !match) return nativeFetch(input, init);
    // Resolve a line number to its current displayed key BEFORE queuing: gift
    // additions/removals must not cause a queued request to modify another item.
    let options = init ? { ...init } : init;
    let requestedSections = [];
    try {
      const body = options?.body;
      const data = typeof body === 'string' ? JSON.parse(body) : null;
      if (data) {
        if (match[1] === 'change' && data.line) {
          const row = document.querySelector(`[data-cart-line="${Number(data.line)}"]`);
          if (row?.dataset.cartKey) { data.id = row.dataset.cartKey; delete data.line; }
        }
        requestedSections = Array.isArray(data.sections) ? data.sections : String(data.sections || '').split(',').filter(Boolean);
        options.body = JSON.stringify(data);
      } else if (body instanceof FormData) {
        requestedSections = String(body.get('sections') || '').split(',').filter(Boolean);
      }
    } catch (_) { /* The original request still receives normal Shopify validation. */ }
    return enqueue(async () => {
      const response = await nativeFetch(input, options);
      if (!response.ok) return response;
      const original = await response.clone().json();
      if (original.errors || original.status >= 400) return response;
      try {
        const cart = await reconcile();
        const ids = requestedSections.length ? requestedSections : sectionIds();
        const html = await sections(ids);
        let data;
        if (match[1] === 'add') data = { ...original, sections: html };
        else data = { ...cart, sections: html };
        if (!requestedSections.length) render(cart, html);
        const headers = new Headers(response.headers);
        headers.delete('content-length'); headers.delete('content-encoding');
        headers.set('content-type', 'application/json');
        return new Response(JSON.stringify(data), { status: response.status, headers });
      } catch (error) { failed(error); throw error; }
    });
  };
  // Capture before Dawn's delegated quantity handler. Gift selects are not quantities.
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-fastlane-gift-variant]');
    if (!select) return;
    event.stopImmediatePropagation();
    const kind = Object.keys(handles).find(k => handles[k] === select.dataset.giftHandle);
    const chosen = Number(select.value);
    enqueue(async () => {
      const cart = await request('cart.js');
      if (!eligible(cart)[kind]) { const next = await reconcile(); render(next, await sections(sectionIds())); return; }
      const old = cart.items.find(i => giftKind(i) === kind);
      if (!old || old.variant_id === chosen) return;
      const product = await request('products/' + handles[kind] + '.js');
      if (!product.variants.some(v => v.id === chosen && v.available)) {
        messages = ['That gift option is sold out. Please choose another.'];
        render(cart, await sections(sectionIds())); return;
      }
      // Keep the old gift recoverable if the new variant sells out mid-request.
      await updates({ [old.key]: 0 });
      let restored = false;
      try {
        await request('cart/add.js', { items: [{ id: chosen, quantity: 1, properties: { _fastlane_gift: kind } }] });
        choices[kind] = chosen;
      } catch (error) {
        await request('cart/add.js', { items: [{ id: old.variant_id, quantity: 1, properties: old.properties || { _fastlane_gift: kind } }] });
        choices[kind] = old.variant_id;
        restored = true;
      }
      const next = await reconcile();
      if (restored) messages.push('That gift option could not be added. Your previous option was kept.');
      render(next, await sections(sectionIds()));
    }).catch(failed);
  }, true);
  const checkoutSelector = '[name="checkout"], .cart__ctas, .es-popup-button, .es-popup-button-cart, .es-popup-button-product, .es-popup-button-overwrite';
  document.addEventListener('click', event => {
    if (!(pending || blocked) || !event.target.closest(checkoutSelector)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (blocked) announce();
  }, true);
  document.addEventListener('submit', event => {
    if ((pending || blocked) && (event.submitter?.name === 'checkout' || event.target.action?.includes('/checkout'))) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  window.FastlaneGifts = {
    paintMessages: announce,
    refresh: () => enqueue(async () => { const cart = await reconcile(); render(cart, await sections(sectionIds())); }).catch(failed)
  };
  // Reconcile returning shoppers and restored back/forward carts, without opening the drawer.
  const start = () => window.FastlaneGifts.refresh();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  window.addEventListener('pageshow', event => { if (event.persisted) start(); });
})();
